import React, { Dispatch, useEffect } from "react";
import { useImmer } from "use-immer";

import {
  IgAccordion,
  IgButton,
  IgDivider,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  StripeApiGetAllPricesByProductIdResponseBodyType,
  StripeApiSubscriptionItemPairDto,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useStripeApi } from "@src/api/stripe/stripe-api";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import { DecimalFormat } from "@src/features/admin/components/utils";
import { PriceLine } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PricesTab/PriceAccordion/PriceLine";
import { TemplateLine } from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/PricesTab/PriceAccordion/TemplateLine";
import {
  CreateOrModifySubscriptionReducerType,
  CreateSubscriptionReducerAction,
} from "@src/features/admin/pages/overview/components/Drawers/SubscriptionDrawer/subscriptionReducer";
import { theme } from "@src/theme";

interface ProductAccordionProps {
  productId: string;
  defaultOpen: boolean;
  items: StripeApiSubscriptionItemPairDto[];
  payload: CreateOrModifySubscriptionReducerType;
  dispatchPayload: Dispatch<CreateSubscriptionReducerAction>;
  allData: StripeApiSubscriptionItemPairDto[];
}

export const PriceAccordion = ({
  productId,
  defaultOpen,
  items,
  allData,
  payload,
  dispatchPayload,
}: ProductAccordionProps) => {
  const [isTemplate, setIsTemplate] = useImmer<boolean>(false);
  const [expanded, setExpanded] = useImmer<boolean>(defaultOpen || false);

  const prices = useStripeApi<StripeApiGetAllPricesByProductIdResponseBodyType>(
    {
      action: "getPricesByProductId",
      productId: productId,
      orgs: payload.orgs,
    }
  );
  useEffect(() => {
    if (!items.length || (items.length === 1 && items[0].price === null)) {
      setIsTemplate(false);
    }
  }, [items]);

  if (!prices.isSuccess) {
    return <SkeletonGroup lines={4} />;
  }

  return (
    <IgAccordion
      expanded={expanded || false}
      onChange={() => {
        setExpanded((draft) => {
          draft = !draft;
          return draft;
        });
      }}
      title={
        <Grid
          item
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          columnSpacing={2}
        >
          <Grid item ml={1.5}>
            <IgTypography igStyle={"semibold"}>
              {items[0].product.name}
            </IgTypography>
          </Grid>
          {items.length > 1 ? (
            <Grid item>
              <IgTypography variant={"body2"}> Varies</IgTypography>
            </Grid>
          ) : items[0].quantity ? (
            <Grid item>
              <IgTypography variant={"body2"}>
                {DecimalFormat.format(items[0].quantity)} orders / month
              </IgTypography>
            </Grid>
          ) : null}
        </Grid>
      }
    >
      <>
        {isTemplate ? null : (
          <TemplateLine
            setIsTemplate={setIsTemplate}
            productId={productId}
            payload={payload}
            dispatchPayload={dispatchPayload}
          />
        )}
        {isTemplate ? null : (
          <Grid item mt={2} mb={1}>
            <IgDivider>
              <IgTypography variant={"body2"} color={"tertiary"}>
                OR
              </IgTypography>
            </IgDivider>
          </Grid>
        )}
        {isTemplate ? null : (
          <Grid item>
            <IgTooltip title="Use individual prices for non volume-based subscriptions.">
              <span>
                <IgTypography variant={"body2"}>Choose Price</IgTypography>
              </span>
            </IgTooltip>
          </Grid>
        )}
        {items.map((item, index) => (
          <PriceLine
            index={index}
            key={`${item.id!}-${index}`}
            item={item}
            items={items}
            allData={allData}
            payload={payload}
            dispatchPayload={dispatchPayload}
          />
        ))}
        {isTemplate ? null : (
          <Grid
            item
            textAlign={"end"}
            sx={{ marginTop: "10px", marginRight: "4px" }}
          >
            <IgButton
              variant={"outlined"}
              disabled={!items.every((i) => !!i.price)}
              onClick={() => {
                dispatchPayload({ type: "addPriceToProduct", productId });
              }}
            >
              Add Another Price
            </IgButton>
          </Grid>
        )}
      </>
    </IgAccordion>
  );
};
